import { useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import { useDispatch } from "react-redux";

import jwtDecode from "jwt-decode";

import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

import PartnerAuthLayout from "layouts/authentication/components/PartnerAuthLayout";

import Validations from "utils/Validations/index";

import { loginThunk } from "redux/Thunks/Authentication";

import Sessions from "utils/Sessions";

import { defaultData } from "utils/Constants";

function SignIn() {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");

  const [emailError, setEmailError] = useState("");

  const [password, setPassword] = useState("");

  const [passwordError, setPasswordError] = useState("");

  const [credentialError, setCredentialError] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const handleShowPasswordClick = () => {
    setShowPassword(!showPassword);
  };

  const handleValidation = () => {
    const emailValidate = Validations.validate("email", email, null, null, true);

    const passwordValidate = Validations.validate("password", password, 6, 30, true);

    if (emailValidate !== "" || passwordValidate !== "") {
      setEmailError(emailValidate);

      setPasswordError(passwordValidate);

      return false;
    }

    return true;
  };

  const handleLogin = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    setLoading(true);

    const val = handleValidation();

    if (val) {
      const body = { email, password, isMobile: false };

      const res = await dispatch(loginThunk(body));

      setLoading(false);

      if (res.payload.status !== 200) {
        setCredentialError(res.payload.data?.message || "Invalid credentials");
      } else {
        setCredentialError("");

        if (email !== "") {
          Sessions.setUserToken(res.payload.data.data.token);

          Sessions.setUserEmail(email);
        }

        const decodedToken = jwtDecode(res.payload.data.data.token);

        if (decodedToken.mobileNumber) Sessions.setMobileNumber(decodedToken.mobileNumber);

        Sessions.setUserFullName(decodedToken.name);

        navigate(
          decodedToken.role === defaultData.SUPER_ADMIN_ROLE ? "admin/home" : "client/setting"
        );
      }
    } else {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);

    setEmailError("");

    setCredentialError("");
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);

    setPasswordError("");

    setCredentialError("");
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleLogin(event);
    }
  };

  return (
    <PartnerAuthLayout>
      {/* Heading */}

      <Box sx={{ mb: 2.5 }}>
        <Typography
          sx={{
            fontSize: { xs: "24px", md: "28px" },

            fontWeight: 800,

            color: "#101828",

            fontFamily: "Inter, sans-serif",

            letterSpacing: "-0.02em",
          }}
        >
          Welcome <span style={{ color: "#1E40AF" }}>Partner!</span>
        </Typography>

        <Typography sx={{ fontSize: "13.5px", color: "#64748B", mt: 0.5 }}>
          Sign in to your Finvibes Partner Portal to continue.
        </Typography>
      </Box>

      {/* Error Message */}

      {credentialError ? (
        <Box
          sx={{
            backgroundColor: "#FEF2F2",

            border: "1px solid #FEE2E2",

            color: "#991B1B",

            px: 2,

            py: 1,

            borderRadius: "8px",

            mb: 2,

            fontSize: "13px",
          }}
        >
          {credentialError}
        </Box>
      ) : null}

      {/* Form */}

      <Box component="form" role="form" onSubmit={handleLogin}>
        {/* Email Input */}

        <Box sx={{ mb: 2 }}>
          <Typography
            component="label"
            htmlFor="email"
            sx={{
              display: "block",

              fontSize: "13px",

              fontWeight: 600,

              color: "#344054",

              mb: 0.6,
            }}
          >
            Email
          </Typography>

          <TextField
            fullWidth
            id="email"
            name="email"
            type="email"
            placeholder="Enter Your Email"
            value={email}
            onChange={handleEmailChange}
            onKeyDown={handleKeyDown}
            error={Boolean(emailError)}
            helperText={emailError}
            InputProps={{
              sx: {
                borderRadius: "8px",

                fontSize: "14px",

                backgroundColor: "#FFFFFF",

                "& fieldset": { borderColor: "#D0D5DD" },

                "&:hover fieldset": { borderColor: "#98A2B3" },

                "&.Mui-focused fieldset": { borderColor: "#1E40AF", borderWidth: "1.5px" },
              },
            }}
            FormHelperTextProps={{ sx: { ml: 0, mt: 0.5, fontSize: "12px", color: "#DC2626" } }}
          />
        </Box>

        {/* Password Input */}

        <Box sx={{ mb: 1 }}>
          <Typography
            component="label"
            htmlFor="password"
            sx={{
              display: "block",

              fontSize: "13px",

              fontWeight: 600,

              color: "#344054",

              mb: 0.6,
            }}
          >
            Password
          </Typography>

          <TextField
            fullWidth
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder="Enter your password"
            value={password}
            onChange={handlePasswordChange}
            onKeyDown={handleKeyDown}
            error={Boolean(passwordError)}
            helperText={passwordError}
            InputProps={{
              sx: {
                borderRadius: "8px",

                fontSize: "14px",

                backgroundColor: "#FFFFFF",

                "& fieldset": { borderColor: "#D0D5DD" },

                "&:hover fieldset": { borderColor: "#98A2B3" },

                "&.Mui-focused fieldset": { borderColor: "#1E40AF", borderWidth: "1.5px" },
              },

              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleShowPasswordClick}
                    edge="end"
                    size="small"
                    sx={{ color: "#98A2B3" }}
                  >
                    {showPassword ? (
                      <VisibilityOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            FormHelperTextProps={{ sx: { ml: 0, mt: 0.5, fontSize: "12px", color: "#DC2626" } }}
          />
        </Box>

        {/* Forgot Password Row */}

        <Box
          sx={{
            display: "flex",

            justifyContent: "flex-end",

            alignItems: "center",

            mt: 1,

            mb: 2.5,
          }}
        >
          <Typography
            component={Link}
            to="/authentication/forgot-password"
            sx={{
              color: "#1E40AF",

              fontSize: "13px",

              fontWeight: 600,

              textDecoration: "none",

              "&:hover": { textDecoration: "underline" },
            }}
          >
            Forgot Password?
          </Typography>
        </Box>

        {/* Log In Button */}

        <Button
          fullWidth
          variant="contained"
          disabled={loading}
          onClick={handleLogin}
          sx={{
            backgroundColor: "#1E40AF",

            color: "#FFFFFF",

            height: "46px",

            borderRadius: "8px",

            fontSize: "15px",

            fontWeight: 700,

            textTransform: "none",

            boxShadow: "none",

            "&:hover": {
              backgroundColor: "#1D3A8A",

              boxShadow: "none",
            },

            "&:disabled": {
              backgroundColor: "#93C5FD",

              color: "#FFFFFF",
            },
          }}
        >
          {loading ? "Logging in..." : "Log In"}
        </Button>

        {/* Or Sign In With Divider */}

        <Box sx={{ display: "flex", alignItems: "center", my: 2 }}>
          <Box sx={{ flex: 1, height: "1px", backgroundColor: "#E2E8F0" }} />

          <Typography sx={{ px: 2, fontSize: "12px", color: "#94A3B8", fontWeight: 500 }}>
            Or Sign In with
          </Typography>

          <Box sx={{ flex: 1, height: "1px", backgroundColor: "#E2E8F0" }} />
        </Box>

        {/* Social Icons (Apple, Google, Microsoft) - Disabled with Upcoming tooltip */}

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 2.5 }}>
          {/* Apple */}

          <Tooltip title="Upcoming" arrow placement="top">
            <Box component="span" sx={{ display: "inline-block", cursor: "not-allowed" }}>
              <IconButton
                disabled
                sx={{
                  width: 42,

                  height: 42,

                  border: "1px solid #E2E8F0",

                  borderRadius: "50%",

                  color: "#000000",

                  opacity: 0.45,

                  pointerEvents: "none",

                  backgroundColor: "#F8FAFC",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.62-.75 1.04-1.8 0.92-2.85-.9.04-1.99.6-2.64 1.36-.57.65-1.07 1.71-.93 2.73 1.01.08 2.03-.49 2.65-1.24z" />
                </svg>
              </IconButton>
            </Box>
          </Tooltip>

          {/* Google */}

          <Tooltip title="Upcoming" arrow placement="top">
            <Box component="span" sx={{ display: "inline-block", cursor: "not-allowed" }}>
              <IconButton
                disabled
                sx={{
                  width: 42,

                  height: 42,

                  border: "1px solid #E2E8F0",

                  borderRadius: "50%",

                  opacity: 0.45,

                  pointerEvents: "none",

                  backgroundColor: "#F8FAFC",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"
                  />

                  <path
                    fill="#34A853"
                    d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"
                  />

                  <path
                    fill="#FBBC05"
                    d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z"
                  />

                  <path
                    fill="#EA4335"
                    d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
                  />
                </svg>
              </IconButton>
            </Box>
          </Tooltip>

          {/* Microsoft */}

          <Tooltip title="Upcoming" arrow placement="top">
            <Box component="span" sx={{ display: "inline-block", cursor: "not-allowed" }}>
              <IconButton
                disabled
                sx={{
                  width: 42,

                  height: 42,

                  border: "1px solid #E2E8F0",

                  borderRadius: "50%",

                  opacity: 0.45,

                  pointerEvents: "none",

                  backgroundColor: "#F8FAFC",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 21 21">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022" />

                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />

                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />

                  <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                </svg>
              </IconButton>
            </Box>
          </Tooltip>
        </Box>

        {/* Terms & Privacy Disclaimer */}

        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontSize: "11.5px", color: "#64748B", lineHeight: 1.5 }}>
            Access to this portal is for authorized partners only.
          </Typography>

          <Typography sx={{ fontSize: "11.5px", color: "#64748B", lineHeight: 1.5 }}>
            By logging in, you agree to our{" "}
            <Typography
              component="a"
              href="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "#1E40AF",

                fontWeight: 600,

                cursor: "pointer",

                fontSize: "11.5px",

                textDecoration: "none",

                "&:hover": { textDecoration: "underline" },
              }}
            >
              Terms & Conditions
            </Typography>{" "}
            and{" "}
            <Typography
              component="a"
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "#1E40AF",

                fontWeight: 600,

                cursor: "pointer",

                fontSize: "11.5px",

                textDecoration: "none",

                "&:hover": { textDecoration: "underline" },
              }}
            >
              Privacy Policy
            </Typography>
            .
          </Typography>
        </Box>
      </Box>
    </PartnerAuthLayout>
  );
}

export default SignIn;
